import React, { useEffect, useState, useCallback } from "react";
import Layout2 from "../../components/global/layout2";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import ReactECharts from "echarts-for-react";
import {
  Table,
  Card,
  Row,
  Col,
  Input,
  Button,
  Select,
  Tag,
  Tooltip,
  Empty,
} from "antd";

const { Option } = Select;

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const PRIMARY_COLOR = "#3E549D";

// Color intensity helper for heatmap cells
const getHeatColor = (value, max) => {
  if (!value || !max) return "transparent";
  const ratio = value / max;
  if (ratio > 0.75) return "#3E549D";
  if (ratio > 0.5) return "#6B7FC4";
  if (ratio > 0.25) return "#A0B0DD";
  if (ratio > 0) return "#D4DBED";
  return "transparent";
};

const getTextColor = (value, max) => {
  if (!value || !max) return "#999";
  const ratio = value / max;
  return ratio > 0.5 ? "#fff" : "#333";
};

const Analytics = () => {
  // Board selector state
  const [boards, setBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState("");

  // Golden Lanes
  const [goldenLanes, setGoldenLanes] = useState([]);
  const [goldenLoading, setGoldenLoading] = useState(false);

  // Lane Patterns
  const [topLanes, setTopLanes] = useState([]);
  const [patternsLoading, setPatternsLoading] = useState(false);

  // Posting Consistency
  const [consistentLanes, setConsistentLanes] = useState([]);
  const [consistencyLoading, setConsistencyLoading] = useState(false);

  // Lane History Lookup
  const [originZip, setOriginZip] = useState("");
  const [destZip, setDestZip] = useState("");
  const [laneHistory, setLaneHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Fetch boards on mount
  useEffect(() => {
    ApiClient.get("boards?status=active").then((res) => {
      if (res.success) {
        setBoards(res?.data?.data || []);
      }
    });
  }, []);

  // Fetch analytics data when board changes
  useEffect(() => {
    fetchGoldenLanes();
    fetchLanePatterns();
    fetchPostingConsistency();
  }, [selectedBoard]);

  const buildParams = (extra = {}) => {
    const params = { ...extra };
    if (selectedBoard) params.board_id = selectedBoard;
    return params;
  };

  // 1. Golden Lanes
  const fetchGoldenLanes = useCallback(() => {
    setGoldenLoading(true);
    ApiClient.get("analytics/golden-lanes", buildParams({ min_count: 5 })).then(
      (res) => {
        setGoldenLoading(false);
        if (res.success) {
          setGoldenLanes(res?.data?.goldenLanes || []);
        } else {
          setGoldenLanes([]);
        }
      }
    );
  }, [selectedBoard]);

  // 2. Lane Patterns
  const fetchLanePatterns = useCallback(() => {
    setPatternsLoading(true);
    ApiClient.get("analytics/lane-patterns", buildParams()).then((res) => {
      setPatternsLoading(false);
      if (res.success) {
        setTopLanes(res?.data?.topLanes || []);
      } else {
        setTopLanes([]);
      }
    });
  }, [selectedBoard]);

  // 3. Posting Consistency
  const fetchPostingConsistency = useCallback(() => {
    setConsistencyLoading(true);
    ApiClient.get("analytics/posting-consistency", buildParams()).then(
      (res) => {
        setConsistencyLoading(false);
        if (res.success) {
          setConsistentLanes(res?.data?.consistentLanes || []);
        } else {
          setConsistentLanes([]);
        }
      }
    );
  }, [selectedBoard]);

  // 4. Lane History Lookup
  const fetchLaneHistory = () => {
    if (!originZip || !destZip) return;
    setHistoryLoading(true);
    ApiClient.get("analytics/lane-history", {
      origin_zip: originZip,
      destination_zip: destZip,
    }).then((res) => {
      setHistoryLoading(false);
      if (res.success) {
        setLaneHistory(res?.data || null);
      } else {
        setLaneHistory(null);
      }
    });
  };

  // ---------- Golden Lanes Table Columns ----------
  const goldenColumns = [
    {
      title: "Lane",
      dataIndex: "lane",
      key: "lane",
      sorter: (a, b) => (a.lane || "").localeCompare(b.lane || ""),
      render: (text) => (
        <span style={{ fontWeight: 600 }}>{text || "N/A"}</span>
      ),
    },
    {
      title: "Frequency",
      dataIndex: "frequency",
      key: "frequency",
      sorter: (a, b) => (a.frequency || 0) - (b.frequency || 0),
      render: (val) => val || 0,
    },
    {
      title: "Consistency",
      dataIndex: "consistency",
      key: "consistency",
      sorter: (a, b) => (a.consistency || 0) - (b.consistency || 0),
      render: (val) => {
        const pct = typeof val === "number" ? (val * 100).toFixed(1) : val;
        return <span>{pct}%</span>;
      },
    },
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
      sorter: (a, b) => (a.score || 0) - (b.score || 0),
      defaultSortOrder: "descend",
      render: (val) => (
        <Tag
          color={val >= 80 ? "green" : val >= 50 ? "gold" : "default"}
          style={{ fontWeight: 600 }}
        >
          {typeof val === "number" ? val.toFixed(1) : val}
        </Tag>
      ),
    },
    {
      title: "Rate",
      key: "rate",
      render: (_, record) => {
        const rate = record?.rateData?.rate;
        return rate ? `$${Number(rate).toLocaleString()}` : "N/A";
      },
      sorter: (a, b) =>
        (a?.rateData?.rate || 0) - (b?.rateData?.rate || 0),
    },
    {
      title: "RPM",
      key: "rpm",
      render: (_, record) => {
        const rpm = record?.rateData?.rpm;
        return rpm ? `$${Number(rpm).toFixed(2)}` : "N/A";
      },
      sorter: (a, b) =>
        (a?.rateData?.rpm || 0) - (b?.rateData?.rpm || 0),
    },
    {
      title: "Confidence",
      key: "confidence",
      render: (_, record) => {
        const cs = record?.rateData?.confidence_score;
        if (cs === undefined || cs === null) return "N/A";
        const color = cs >= 0.7 ? "green" : cs >= 0.4 ? "gold" : "red";
        return (
          <Tag color={color}>
            {(cs * 100).toFixed(0)}%
          </Tag>
        );
      },
      sorter: (a, b) =>
        (a?.rateData?.confidence_score || 0) -
        (b?.rateData?.confidence_score || 0),
    },
  ];

  // ---------- Lane Frequency Chart Options ----------
  const getFrequencyChartOption = () => {
    const chartData = (topLanes || []).slice(0, 20);
    const lanes = chartData.map((item) => item.lane || "Unknown");
    const counts = chartData.map((item) => item.count || 0);

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
      },
      grid: {
        left: "3%",
        right: "6%",
        bottom: "3%",
        top: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "value",
        axisLabel: { fontSize: 11 },
      },
      yAxis: {
        type: "category",
        data: lanes.reverse(),
        axisLabel: {
          fontSize: 11,
          width: 180,
          overflow: "truncate",
        },
        inverse: false,
      },
      series: [
        {
          name: "Count",
          type: "bar",
          data: counts.reverse(),
          itemStyle: {
            color: PRIMARY_COLOR,
            borderRadius: [0, 4, 4, 0],
          },
          barMaxWidth: 24,
        },
      ],
    };
  };

  // ---------- Posting Consistency Heatmap Table ----------
  const heatmapMaxValue = consistentLanes.reduce((max, lane) => {
    const dayVals = DAYS.map((d) => lane?.dayDistribution?.[d] || 0);
    return Math.max(max, ...dayVals);
  }, 0);

  const consistencyColumns = [
    {
      title: "Lane",
      dataIndex: "lane",
      key: "lane",
      fixed: "left",
      width: 220,
      render: (text) => (
        <span style={{ fontWeight: 600, fontSize: 12 }}>{text || "N/A"}</span>
      ),
    },
    {
      title: "Posts",
      dataIndex: "total_posts",
      key: "total_posts",
      width: 70,
      sorter: (a, b) => (a.total_posts || 0) - (b.total_posts || 0),
    },
    ...DAYS.map((day) => ({
      title: day,
      key: day,
      width: 60,
      align: "center",
      render: (_, record) => {
        const val = record?.dayDistribution?.[day] || 0;
        return (
          <div
            style={{
              backgroundColor: getHeatColor(val, heatmapMaxValue),
              color: getTextColor(val, heatmapMaxValue),
              borderRadius: 4,
              padding: "4px 0",
              fontWeight: 600,
              fontSize: 12,
              textAlign: "center",
              minWidth: 32,
            }}
          >
            {val || "-"}
          </div>
        );
      },
    })),
    {
      title: "Score",
      dataIndex: "consistency_score",
      key: "consistency_score",
      width: 80,
      sorter: (a, b) =>
        (a.consistency_score || 0) - (b.consistency_score || 0),
      defaultSortOrder: "descend",
      render: (val) => (
        <Tag
          color={val >= 0.7 ? "green" : val >= 0.4 ? "gold" : "default"}
        >
          {typeof val === "number" ? (val * 100).toFixed(0) + "%" : "N/A"}
        </Tag>
      ),
    },
  ];

  // ---------- Lane History Day Distribution Chart ----------
  const getLaneHistoryDayChart = () => {
    if (!laneHistory?.dayDistribution) return null;
    const dist = laneHistory.dayDistribution;
    return {
      tooltip: { trigger: "axis" },
      xAxis: {
        type: "category",
        data: DAYS,
        axisLabel: { fontSize: 12 },
      },
      yAxis: {
        type: "value",
        axisLabel: { fontSize: 11 },
      },
      series: [
        {
          name: "Posts",
          type: "bar",
          data: DAYS.map((d) => dist[d] || 0),
          itemStyle: {
            color: PRIMARY_COLOR,
            borderRadius: [4, 4, 0, 0],
          },
          barMaxWidth: 40,
        },
      ],
      grid: { left: "3%", right: "3%", bottom: "3%", top: "10%", containLabel: true },
    };
  };

  // ---------- Lane History Hour Distribution Chart ----------
  const getLaneHistoryHourChart = () => {
    if (!laneHistory?.hourDistribution) return null;
    const hours = laneHistory.hourDistribution;
    return {
      tooltip: { trigger: "axis" },
      xAxis: {
        type: "category",
        data: hours.map((_, i) => `${i}:00`),
        axisLabel: { fontSize: 10, rotate: 45 },
      },
      yAxis: {
        type: "value",
        axisLabel: { fontSize: 11 },
      },
      series: [
        {
          name: "Posts",
          type: "line",
          data: hours,
          smooth: true,
          areaStyle: { opacity: 0.15 },
          lineStyle: { color: PRIMARY_COLOR },
          itemStyle: { color: PRIMARY_COLOR },
        },
      ],
      grid: { left: "3%", right: "3%", bottom: "8%", top: "10%", containLabel: true },
    };
  };

  return (
    <Layout2 title="Lane Analytics">
      <div className="mainareas">
        <div className="edit-page-common">
          <div className="white-bg-main p-4">
            {/* Page Header */}
            <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
              <div>
                <h3
                  className="mb-1"
                  style={{ color: PRIMARY_COLOR, fontWeight: 700 }}
                >
                  Lane Analytics Dashboard
                </h3>
                <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                  Analyze lane performance, frequency, and posting patterns
                </p>
              </div>
              <div style={{ minWidth: 240 }}>
                <Select
                  placeholder="All Boards"
                  allowClear
                  style={{ width: "100%" }}
                  value={selectedBoard || undefined}
                  onChange={(val) => setSelectedBoard(val || "")}
                  size="large"
                >
                  {boards.map((b) => (
                    <Option key={b.id || b._id} value={b.id || b._id}>
                      {b.name || b.board_name || b.id}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Section 1: Golden Lanes */}
            <Card
              title={
                <span style={{ color: PRIMARY_COLOR, fontWeight: 600 }}>
                  Golden Lanes
                </span>
              }
              className="mb-4"
              bordered={false}
              style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
              extra={
                <Tag color="gold" style={{ fontWeight: 500 }}>
                  Top performing lanes by composite score
                </Tag>
              }
            >
              <Table
                columns={goldenColumns}
                dataSource={goldenLanes}
                loading={goldenLoading}
                rowKey={(r, i) => r.lane + "_" + i}
                pagination={{ pageSize: 10, showSizeChanger: true }}
                size="middle"
                scroll={{ x: 900 }}
                locale={{ emptyText: <Empty description="No golden lanes found" /> }}
              />
            </Card>

            {/* Section 2: Lane Frequency Chart */}
            <Card
              title={
                <span style={{ color: PRIMARY_COLOR, fontWeight: 600 }}>
                  Lane Frequency - Top 20
                </span>
              }
              className="mb-4"
              bordered={false}
              style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
              extra={
                <Tag color="blue" style={{ fontWeight: 500 }}>
                  Most posted lanes by count
                </Tag>
              }
            >
              {patternsLoading ? (
                <div className="text-center py-5">
                  <span className="text-muted">Loading...</span>
                </div>
              ) : topLanes.length > 0 ? (
                <ReactECharts
                  option={getFrequencyChartOption()}
                  style={{
                    height: Math.max(300, Math.min(topLanes.length, 20) * 32),
                    width: "100%",
                  }}
                  notMerge={true}
                />
              ) : (
                <Empty description="No lane pattern data available" />
              )}
            </Card>

            {/* Section 3: Posting Consistency Heatmap */}
            <Card
              title={
                <span style={{ color: PRIMARY_COLOR, fontWeight: 600 }}>
                  Posting Consistency Heatmap
                </span>
              }
              className="mb-4"
              bordered={false}
              style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
              extra={
                <Tag color="purple" style={{ fontWeight: 500 }}>
                  Day-of-week distribution per lane
                </Tag>
              }
            >
              <Table
                columns={consistencyColumns}
                dataSource={consistentLanes}
                loading={consistencyLoading}
                rowKey={(r, i) => r.lane + "_cons_" + i}
                pagination={{ pageSize: 10, showSizeChanger: true }}
                size="small"
                scroll={{ x: 800 }}
                locale={{
                  emptyText: (
                    <Empty description="No posting consistency data available" />
                  ),
                }}
              />
              {/* Heatmap Legend */}
              {consistentLanes.length > 0 && (
                <div className="d-flex align-items-center gap-2 mt-3">
                  <span style={{ fontSize: 12, color: "#999" }}>Intensity:</span>
                  <div className="d-flex align-items-center gap-1">
                    {[
                      { label: "Low", color: "#D4DBED" },
                      { label: "Med", color: "#A0B0DD" },
                      { label: "High", color: "#6B7FC4" },
                      { label: "Peak", color: "#3E549D" },
                    ].map((item) => (
                      <Tooltip key={item.label} title={item.label}>
                        <div
                          style={{
                            width: 24,
                            height: 16,
                            backgroundColor: item.color,
                            borderRadius: 3,
                          }}
                        />
                      </Tooltip>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* Section 4: Rate Lookup / Lane History */}
            <Card
              title={
                <span style={{ color: PRIMARY_COLOR, fontWeight: 600 }}>
                  Lane History Lookup
                </span>
              }
              bordered={false}
              style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
              extra={
                <Tag color="green" style={{ fontWeight: 500 }}>
                  Search by origin and destination ZIP
                </Tag>
              }
            >
              <Row gutter={16} className="mb-3">
                <Col xs={24} sm={8} md={6}>
                  <label
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Origin ZIP
                  </label>
                  <Input
                    placeholder="e.g. 60601"
                    value={originZip}
                    onChange={(e) => setOriginZip(e.target.value)}
                    onPressEnter={fetchLaneHistory}
                    maxLength={10}
                    size="large"
                  />
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <label
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Destination ZIP
                  </label>
                  <Input
                    placeholder="e.g. 30301"
                    value={destZip}
                    onChange={(e) => setDestZip(e.target.value)}
                    onPressEnter={fetchLaneHistory}
                    maxLength={10}
                    size="large"
                  />
                </Col>
                <Col xs={24} sm={8} md={4} className="d-flex align-items-end">
                  <Button
                    type="primary"
                    size="large"
                    onClick={fetchLaneHistory}
                    loading={historyLoading}
                    disabled={!originZip || !destZip}
                    style={{
                      backgroundColor: PRIMARY_COLOR,
                      borderColor: PRIMARY_COLOR,
                      width: "100%",
                    }}
                  >
                    Search
                  </Button>
                </Col>
              </Row>

              {/* Lane History Results */}
              {laneHistory && (
                <div className="mt-3">
                  {/* Stats Summary */}
                  <Row gutter={16} className="mb-3">
                    <Col xs={12} sm={6}>
                      <Card
                        size="small"
                        style={{
                          textAlign: "center",
                          borderLeft: `3px solid ${PRIMARY_COLOR}`,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            color: "#999",
                            marginBottom: 4,
                          }}
                        >
                          Posting Frequency
                        </div>
                        <div
                          style={{
                            fontSize: 22,
                            fontWeight: 700,
                            color: PRIMARY_COLOR,
                          }}
                        >
                          {laneHistory.postingFrequency ?? "N/A"}
                        </div>
                      </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Card
                        size="small"
                        style={{
                          textAlign: "center",
                          borderLeft: "3px solid #52c41a",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            color: "#999",
                            marginBottom: 4,
                          }}
                        >
                          Avg Distance
                        </div>
                        <div
                          style={{
                            fontSize: 22,
                            fontWeight: 700,
                            color: "#52c41a",
                          }}
                        >
                          {laneHistory.distanceStats?.avg
                            ? `${Math.round(laneHistory.distanceStats.avg)} mi`
                            : "N/A"}
                        </div>
                      </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Card
                        size="small"
                        style={{
                          textAlign: "center",
                          borderLeft: "3px solid #faad14",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            color: "#999",
                            marginBottom: 4,
                          }}
                        >
                          Avg Weight
                        </div>
                        <div
                          style={{
                            fontSize: 22,
                            fontWeight: 700,
                            color: "#faad14",
                          }}
                        >
                          {laneHistory.weightStats?.avg
                            ? `${Math.round(laneHistory.weightStats.avg)} lbs`
                            : "N/A"}
                        </div>
                      </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Card
                        size="small"
                        style={{
                          textAlign: "center",
                          borderLeft: "3px solid #f5222d",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            color: "#999",
                            marginBottom: 4,
                          }}
                        >
                          Max Distance
                        </div>
                        <div
                          style={{
                            fontSize: 22,
                            fontWeight: 700,
                            color: "#f5222d",
                          }}
                        >
                          {laneHistory.distanceStats?.max
                            ? `${Math.round(laneHistory.distanceStats.max)} mi`
                            : "N/A"}
                        </div>
                      </Card>
                    </Col>
                  </Row>

                  {/* Charts */}
                  <Row gutter={16}>
                    {laneHistory.dayDistribution && (
                      <Col xs={24} md={12}>
                        <Card
                          size="small"
                          title={
                            <span style={{ fontSize: 13, fontWeight: 600 }}>
                              Day Distribution
                            </span>
                          }
                          bordered={false}
                          style={{
                            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                          }}
                        >
                          <ReactECharts
                            option={getLaneHistoryDayChart()}
                            style={{ height: 240 }}
                            notMerge={true}
                          />
                        </Card>
                      </Col>
                    )}
                    {laneHistory.hourDistribution &&
                      laneHistory.hourDistribution.length > 0 && (
                        <Col xs={24} md={12}>
                          <Card
                            size="small"
                            title={
                              <span style={{ fontSize: 13, fontWeight: 600 }}>
                                Hour Distribution
                              </span>
                            }
                            bordered={false}
                            style={{
                              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                            }}
                          >
                            <ReactECharts
                              option={getLaneHistoryHourChart()}
                              style={{ height: 240 }}
                              notMerge={true}
                            />
                          </Card>
                        </Col>
                      )}
                  </Row>
                </div>
              )}

              {/* No results state */}
              {!laneHistory && !historyLoading && originZip && destZip && (
                <Empty
                  description="Enter ZIP codes and click Search to view lane history"
                  className="py-4"
                />
              )}
            </Card>
          </div>
        </div>
      </div>
    </Layout2>
  );
};

export default Analytics;
